import React, { FC, useMemo, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { Image } from '@/atoms/Image'
import { slugs } from '@/constants'
import { ReservationObject, TheatricalReleaseObject } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { TKOKToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/TKOKToolTip'
import { BannerPlaceholder } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/BannerPlaceholder'
import ChangeReservationBanner from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/ChangeReservationBanner'
import { TicketPromoBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner/TicketPromoBanner'
import { isAngelIntegratedCountry } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

interface tkokPromosProps {
  mobile: string
  tablet: string
  desktop: string
  desktopOverlay?: React.ReactNode
  tabletOverlay?: React.ReactNode
  mobileOverlay?: React.ReactNode
}

const TOOLTIP_CONFIGS = {
  desktop: {
    containerStyles: 'absolute inset-x-0 bottom-0 p-0 @container/banner',
    wrapperStyles:
      'text-white @xs/banner:ml-[3%] @xs/banner:pb-[3%] @lg/banner:ml-[0] @lg/banner:pb-[0] @xl/banner:ml-[0.5%] @xl/banner:pb-[0.5%] @3xl/banner:ml-[1%] @3xl/banner:pb-[1%] @4xl/banner:ml-[0.5%] @4xl/banner:pb-[0.5%] @7xl/banner:ml-[1%] @7xl/banner:pb-[1%]',
    buttonStyles: `text-xs @lg/banner:text-[10px] @2xl/banner:text-sm @3xl/banner:text-md @5xl/banner:text-lg @7xl/banner:text-xl !ring-0 !ring-transparent`,
  },
  tablet: {
    containerStyles: 'absolute inset-x-0 bottom-0 p-0 @container/banner',
    wrapperStyles: 'ml-[2%] pb-[2%] text-white @xl/banner:ml-[3%] @xl/banner:pb-[3%]',
    buttonStyles: `text-base !ring-0 !ring-transparent`,
  },
  mobile: {
    containerStyles: 'absolute inset-x-0 bottom-0 p-0 @container/banner',
    wrapperStyles: 'ml-[3%] pb-[3%] text-white',
    buttonStyles: `text-[11px] @md/banner:text-sm !ring-0 !ring-transparent`,
  },
}

const createOverlay = (size: 'desktop' | 'tablet' | 'mobile') => {
  const config = TOOLTIP_CONFIGS[size]
  return (
    <div className={config.containerStyles}>
      <div className={config.wrapperStyles}>
        <TKOKToolTip buttonClassName={config.buttonStyles} />
      </div>
    </div>
  )
}

export const otherBanners: Record<string, tkokPromosProps> = {
  guildTickets: {
    mobile: '/v1741968772/TKOK_BuyEarlySaveBig_Banners_R2_856x286_v4_cb7d9e.webp',
    tablet: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_1200x300_v4_ssypuz.webp',
    desktop: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_2000x334_v4_e54kzk.webp',
  },
  kidsgofreevideo: {
    mobile: '/v1743548425/king-of-kings/tkok-kids-free-banner-2x-mobile-02.webp',
    tablet: '/v1743548425/king-of-kings/tkok-kids-free-banner-2x-tablet-02.webp',
    desktop: '/v1743548425/king-of-kings/tkok-kids-free-banner-2x-desk-02.webp',
  },
}
export const tkokPromos: Record<string, tkokPromosProps> = {
  buy4get2: {
    mobile: '/v1741968772/TKOK_BuyEarlySaveBig_Banners_R2_856x286_v4_cb7d9e.webp',
    tablet: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_1200x300_v4_ssypuz.webp',
    desktop: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_2000x334_v4_e54kzk.webp',
  },
  buy4get1: {
    mobile: '/v1741968772/TKOK_BuyEarlySaveBig_Banners_R2_856x286_v3_nvlugc.webp',
    tablet: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_1200x300_v3_life9u.webp',
    desktop: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_2000x334_v3_jzguia.webp',
  },
  buy3get2: {
    mobile: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_856x286_v2_wvrouq.webp',
    tablet: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_1200x300_v2_jgpw5f.webp',
    desktop: '/v1741968772/TKOK_BuyEarlySaveBig_Banners_R2_2000x334_v2_q7a69q.webp',
  },
  buy3get1: {
    mobile: '/v1741968771/TKOK_BuyEarlySaveBig_Banners_R2_856x286_v1_ior3nh.webp',
    tablet: '/v1741968772/TKOK_BuyEarlySaveBig_Banners_R2_1200x300_v1_nsdhvh.webp',
    desktop: '/v1741968772/TKOK_BuyEarlySaveBig_Banners_R2_2000x334_v1_dc3dxd.webp',
  },
  kidsgofree: {
    mobile: '/v1743181097/king-of-kings/KOK_Kids-Go-Free_PIF_Mobile_856x286.webp',
    tablet: '/v1743181097/king-of-kings/KOK_Kids-Go-Free_PIF_Tablet_1200x300.webp',
    desktop: '/v1743181097/king-of-kings/KOK_Kids-Go-Free_PIF_Banner_1.webp',
  },
  kidsgofreealt: {
    mobile: '/v1743453944/king-of-kings/tkok-kids-free-banner-2x-mobile-01-nt.webp',
    tablet: '/v1743453946/king-of-kings/tkok-kids-free-banner-2x-tablet-01-nt.webp',
    desktop: '/v1743453947/king-of-kings/tkok-kids-free-banner-2x-desk-01-nt.webp',
    desktopOverlay: createOverlay('desktop'),
    tabletOverlay: createOverlay('tablet'),
    mobileOverlay: createOverlay('mobile'),
  },
  kidsthursday: {
    mobile: '/v1743278468/king-of-kings/KOK_Kids_PIF_Banners_THURSDAYMobile_856x286.webp',
    tablet: '/v1743278468/king-of-kings/KOK_Kids_PIF_Banners_THURSDAYTablet_1200x300.webp',
    desktop: '/v1743278468/king-of-kings/KOK_Kids_PIF_Banners_THURSDAYBanner_2000x334.webp',
  },
  joyride: {
    mobile: '/v1743532969/king-of-kings/tkok-kids-free-joyride-banner-2x-mobile-01.webp',
    tablet: '/v1743532969/king-of-kings/tkok-kids-free-joyride-banner-2x-tablet-01.webp',
    desktop: '/v1743532968/king-of-kings/tkok-kids-free-joyride-banner-2x-desk-01.webp',
  },
}

const renderPromoBanner = (promoConfig: tkokPromosProps) => (
  <div className="relative mb-2 @container">
    <div className="relative hidden cursor-pointer lg:block">
      <Image className="h-full w-full rounded-xl" src={promoConfig.desktop} alt={''} width={2000} height={334} />
      {promoConfig.desktopOverlay}
    </div>
    <div className="relative hidden cursor-pointer md:block lg:hidden">
      <Image className="h-full w-full rounded-xl" src={promoConfig.tablet} alt={''} width={1200} height={300} />
      {promoConfig.tabletOverlay}
    </div>
    <div className="relative md:hidden">
      <Image src={promoConfig.mobile} alt={''} className="h-full w-full rounded-lg" width={856} height={286} />
      {promoConfig.mobileOverlay}
    </div>
  </div>
)

interface BannerSectionProps {
  reservation?: ReservationObject
  isEditReservationFlow: boolean
  theatricalRelease: TheatricalReleaseObject
}

const TicketBannerSection: FC<BannerSectionProps> = ({ reservation, isEditReservationFlow, theatricalRelease }) => {
  const { t } = useTranslate('tickets')
  const { query, isReady } = useRouter()
  const { isFreeTicketPromo } = useTicketsContext()
  const { experiments } = useTheatricalPromotionContext()
  const countryCode = theatricalRelease?.region?.countryCode
  const { areGuildTicketsAvailable, loading: guildTicketsLoading } = useGuildTickets(theatricalRelease?.theatricalSlug)
  const [isClient, setIsClient] = useState(false)
  useEffect(() => {
    setIsClient(true)
  }, [])

  const queryTkokPromo = useMemo(() => {
    const queryPromo = getItemOrFirstEntry(query?.promo)
    if (!queryPromo || theatricalRelease?.theatricalSlug !== slugs.kingOfKings) return null

    return tkokPromos[queryPromo.toLowerCase()]
  }, [query?.promo, theatricalRelease?.theatricalSlug])

  if (!isReady) {
    return null
  }
  if (guildTicketsLoading && isClient) {
    return <BannerPlaceholder />
  }
  const kidsGoFreeExp = experiments ? experiments['kidsgofree-tooltip'] : undefined
  return (
    <>
      {kidsGoFreeExp !== undefined ? (
        kidsGoFreeExp ? (
          renderPromoBanner(tkokPromos['kidsgofreealt'])
        ) : (
          renderPromoBanner(tkokPromos['kidsgofree'])
        )
      ) : (
        <>
          {(areGuildTicketsAvailable || !queryTkokPromo) && !isEditReservationFlow && !isFreeTicketPromo && (
            <TicketPromoBanner
              theatricalSlug={theatricalRelease?.theatricalSlug}
              areGuildTicketsAvailable={areGuildTicketsAvailable}
              isGuildDataLoading={guildTicketsLoading}
              className="mb-4 rounded-xl !pr-0"
            />
          )}

          {reservation?.priorReservation?.id &&
            reservation?.isChangeReservation &&
            isAngelIntegratedCountry(countryCode) && (
              <ChangeReservationBanner
                description={t('pleaseSelectANewShowtimeBelow', 'Please select a new showtime below.')}
                priorReservation={reservation?.priorReservation}
                step={1}
              />
            )}
          {!areGuildTicketsAvailable &&
            queryTkokPromo &&
            (getItemOrFirstEntry(query?.promo) === 'kidsgofree'
              ? kidsGoFreeExp
                ? renderPromoBanner(tkokPromos['kidsgofreealt'])
                : renderPromoBanner(queryTkokPromo)
              : renderPromoBanner(queryTkokPromo))}
        </>
      )}
    </>
  )
}

export default TicketBannerSection
